import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { adminListProducts, adminSaveProduct, adminDeleteProduct, adminUploadProductImage, adminImportCatalog, adminListProductRelations, adminSaveProductRelations, adminImportGtinCsv } from "@/lib/admin-dashboard.functions";
import { adminListGtinSuggestions, adminGenerateGtinSuggestions, adminApproveGtinSuggestion, adminRejectGtinSuggestion } from "@/lib/admin-gtin-suggestions.functions";
import { Pencil, Trash2, Plus, X, Star, ImageIcon, Upload, GripVertical, ThumbsUp, ThumbsDown, Sparkles, Check, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";


export const Route = createFileRoute("/_authenticated/admin/producten")({
  head: () => ({ meta: [{ title: "Admin — Producten" }, { name: "robots", content: "noindex" }] }),
  component: AdminProducts,
});

function parseGtinCsv(text: string): { sku: string; gtin?: string; lens_mm?: string; color?: string }[] {
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim().length > 0);
  if (lines.length === 0) return [];
  const splitRow = (line: string): string[] => {
    const out: string[] = [];
    let cur = "";
    let quoted = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (quoted) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') quoted = false;
        else cur += ch;
      } else {
        if (ch === '"') quoted = true;
        else if (ch === "," || ch === ";" || ch === "\t") { out.push(cur); cur = ""; }
        else cur += ch;
      }
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const headers = splitRow(lines[0]).map((h) => h.toLowerCase());
  const idxSku = headers.indexOf("sku");
  const idxGtin = headers.findIndex((h) => h === "gtin" || h === "ean");
  const idxLens = headers.indexOf("lens_mm");
  const idxColor = headers.indexOf("color");
  if (idxSku < 0) return [];
  const rows: { sku: string; gtin?: string; lens_mm?: string; color?: string }[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = splitRow(lines[i]);
    const sku = cells[idxSku];
    if (!sku) continue;
    const row: { sku: string; gtin?: string; lens_mm?: string; color?: string } = { sku };
    if (idxGtin >= 0 && cells[idxGtin]) row.gtin = cells[idxGtin];
    if (idxLens >= 0 && cells[idxLens]) row.lens_mm = cells[idxLens];
    if (idxColor >= 0 && cells[idxColor]) row.color = cells[idxColor];
    rows.push(row);
  }
  return rows;
}

type Spec = { label: string; value: string };

type ProductRow = {
  id: string;
  name: string;
  description: string | null;
  price_cents: number;
  gross_price_cents: number;
  cost_price_cents: number;
  unit: string | null;
  category: string | null;
  is_active: boolean;
  updated_at: string;
  channable_last_synced_at: string | null;
  channable_last_error: string | null;
  slug: string | null;
  brand: string | null;
  sku: string | null;
  stock: number | null;
  image_url: string | null;
  images: string[] | null;
  seo_title: string | null;
  seo_description: string | null;
  pros: string[] | null;
  cons: string[] | null;
  specifications: Spec[] | null;
};

type FormState = {
  id?: string;
  name: string;
  description: string;
  priceEur: string;
  grossPriceEur: string;
  costPriceEur: string;
  unit: string;
  category: string;
  is_active: boolean;
  slug: string;
  brand: string;
  sku: string;
  stock: string;
  image_url: string;
  images: string[];
  seo_title: string;
  seo_description: string;
  pros: string[];
  cons: string[];
  specifications: Spec[];
};

const emptyForm: FormState = {
  name: "", description: "", priceEur: "", grossPriceEur: "", costPriceEur: "", unit: "stuk", category: "", is_active: true,
  slug: "", brand: "", sku: "", stock: "",
  image_url: "", images: [],
  seo_title: "", seo_description: "",
  pros: [], cons: [], specifications: [],
};


function slugify(s: string) {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function SortableGalleryImage({ url, isPrimary, onSetPrimary, onRemove }: {
  url: string;
  isPrimary: boolean;
  onSetPrimary: (url: string) => void;
  onRemove: (url: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group rounded-lg border overflow-hidden cursor-pointer transition ${
        isPrimary ? "ring-2 ring-amber-500 ring-offset-2" : "hover:ring-2 hover:ring-primary/40"
      } ${isDragging ? "opacity-50" : ""}`}
      onClick={() => !isPrimary && onSetPrimary(url)}
      title={isPrimary ? "Hoofdafbeelding" : "Klik om hoofdafbeelding te maken, sleep om te herschikken"}
    >
      <div className="aspect-square bg-muted">
        <img src={url} alt="" className="w-full h-full object-cover" />
      </div>
      {isPrimary && (
        <div className="absolute top-1 left-1 bg-amber-500 text-white rounded-full p-0.5">
          <Star className="size-3 fill-white" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition" />
      <button
        type="button"
        className="absolute top-1 right-1 bg-white/90 hover:bg-white text-destructive rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition shadow-sm z-10"
        onClick={(e) => { e.stopPropagation(); onRemove(url); }}
        title="Verwijderen"
      >
        <X className="size-3" />
      </button>
      <div
        {...attributes}
        {...listeners}
        className="absolute bottom-1 left-1 bg-white/90 hover:bg-white rounded-full p-0.5 shadow-sm cursor-grab active:cursor-grabbing z-10"
        title="Sleep om te herschikken"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="size-3 text-muted-foreground" />
      </div>
    </div>
  );
}

function RelationPicker({
  title, description, allProducts, excludeId, selectedIds, onChange, search, onSearch,
}: {
  title: string;
  description: string;
  allProducts: ProductRow[];
  excludeId: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  search: string;
  onSearch: (s: string) => void;
}) {
  const byId = new Map(allProducts.map((p) => [p.id, p]));
  const q = search.trim().toLowerCase();
  const suggestions = allProducts
    .filter((p) => p.id !== excludeId && !selectedIds.includes(p.id))
    .filter((p) => !q || p.name.toLowerCase().includes(q) || (p.sku ?? "").toLowerCase().includes(q) || (p.brand ?? "").toLowerCase().includes(q))
    .slice(0, 8);

  const add = (id: string) => onChange([...selectedIds, id]);
  const remove = (id: string) => onChange(selectedIds.filter((x) => x !== id));
  const move = (idx: number, dir: -1 | 1) => {
    const next = [...selectedIds];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold">{title}</h4>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {selectedIds.length === 0 ? (
        <div className="border rounded p-3 text-xs text-muted-foreground">Nog niets gekoppeld</div>
      ) : (
        <ul className="space-y-1">
          {selectedIds.map((id, i) => {
            const p = byId.get(id);
            return (
              <li key={id} className="flex items-center gap-2 border rounded px-2 py-1.5 bg-white">
                <GripVertical className="size-3 text-muted-foreground" />
                <span className="flex-1 text-sm truncate">{p?.name ?? id}</span>
                {p?.sku && <span className="text-xs text-muted-foreground">{p.sku}</span>}
                <Button type="button" size="sm" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0}>↑</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => move(i, 1)} disabled={i === selectedIds.length - 1}>↓</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => remove(id)}><X className="size-3" /></Button>
              </li>
            );
          })}
        </ul>
      )}
      <div>
        <Input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Zoek product om te koppelen…"
        />
        {q && suggestions.length > 0 && (
          <div className="mt-1 border rounded bg-white max-h-60 overflow-y-auto">
            {suggestions.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { add(p.id); onSearch(""); }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted/40 flex items-center gap-2"
              >
                <Plus className="size-3" />
                <span className="flex-1 truncate">{p.name}</span>
                {p.sku && <span className="text-xs text-muted-foreground">{p.sku}</span>}
              </button>
            ))}
          </div>
        )}
        {q && suggestions.length === 0 && (
          <p className="text-xs text-muted-foreground mt-1">Geen resultaten</p>
        )}
      </div>
    </div>
  );
}

function AdminProducts() {
  const { isAdmin, loading } = useAuth();
  const list = useServerFn(adminListProducts);
  const save = useServerFn(adminSaveProduct);
  const del = useServerFn(adminDeleteProduct);
  const upload = useServerFn(adminUploadProductImage);
  const importCatalog = useServerFn(adminImportCatalog);
  const importGtin = useServerFn(adminImportGtinCsv);
  const [gtinResult, setGtinResult] = useState<{
    updated: number;
    total: number;
    notFound: string[];
    errors: { sku: string; message: string; missing?: string[] }[];
    duplicates: { sku: string; gtin: string; conflictsWith: string }[];
    skipped: { sku: string; reason: string }[];
  } | null>(null);
  const [gtinBusy, setGtinBusy] = useState(false);
  const listRelations = useServerFn(adminListProductRelations);
  const saveRelations = useServerFn(adminSaveProductRelations);

  // EAN-suggesties
  const listGtinSug = useServerFn(adminListGtinSuggestions);
  const genGtinSug = useServerFn(adminGenerateGtinSuggestions);
  const approveSug = useServerFn(adminApproveGtinSuggestion);
  const rejectSug = useServerFn(adminRejectGtinSuggestion);
  const [sugOpen, setSugOpen] = useState(false);
  const [sugStatus, setSugStatus] = useState<"pending" | "approved" | "rejected" | "all">("pending");
  const [sugBusy, setSugBusy] = useState(false);
  const sugQ = useQuery({
    queryKey: ["admin-gtin-suggestions", sugStatus],
    queryFn: () => listGtinSug({ data: { status: sugStatus } }),
    enabled: sugOpen,
  });

  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [newImage, setNewImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [specFilters, setSpecFilters] = useState<Record<string, Set<string>>>({});
  const [sortBy, setSortBy] = useState<string>("updated_desc");
  const [recommendedIds, setRecommendedIds] = useState<string[]>([]);
  const [accessoryIds, setAccessoryIds] = useState<string[]>([]);
  const [relationSearch, setRelationSearch] = useState("");
  const pageSize = 10;



  const { data, isLoading, error: queryError } = useQuery({
    queryKey: ["admin-products"],
    queryFn: () => list({}),
    enabled: isAdmin,
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const result = await save({
        data: {
          id: form.id,
          name: form.name.trim(),
          description: form.description.trim() || null,
          price_cents: Math.round(parseFloat(form.priceEur.replace(",", ".")) * 100),
          gross_price_cents: Math.round(parseFloat(form.grossPriceEur.replace(",", ".")) * 100),
          cost_price_cents: Math.round(parseFloat(form.costPriceEur.replace(",", ".")) * 100),
          unit: form.unit.trim() || null,
          category: form.category.trim() || null,
          is_active: form.is_active,
          slug: form.slug.trim() || null,
          brand: form.brand.trim() || null,
          sku: form.sku.trim() || null,
          stock: form.stock.trim() === "" ? null : parseInt(form.stock, 10),
          image_url: form.image_url.trim() || (form.images[0] ?? null),
          images: form.images,
          seo_title: form.seo_title.trim() || null,
          seo_description: form.seo_description.trim() || null,
          pros: form.pros,
          cons: form.cons,
          specifications: form.specifications,
        },
      });
      const productId = (result as { id?: string })?.id ?? form.id;
      if (productId) {
        await saveRelations({ data: { product_id: productId, relation_type: "recommended", related_ids: recommendedIds } });
        await saveRelations({ data: { product_id: productId, relation_type: "accessory", related_ids: accessoryIds } });
      }
      return result;
    },
    onSuccess: () => {
      toast.success("Opgeslagen");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-products"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Verwijderd");
      qc.invalidateQueries({ queryKey: ["admin-products"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const products = useMemo(() => (data?.products ?? []) as ProductRow[], [data]);


  // Aggregate all spec label → unique values across products (for filter UI)
  const specOptions = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const p of products) {
      for (const s of p.specifications ?? []) {
        if (!s?.label || !s?.value) continue;
        if (!map.has(s.label)) map.set(s.label, new Set());
        map.get(s.label)!.add(s.value);
      }
    }
    return Array.from(map.entries())
      .map(([label, values]) => ({ label, values: Array.from(values).sort((a, b) => a.localeCompare(b, "nl")) }))
      .sort((a, b) => a.label.localeCompare(b.label, "nl"));
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const specEntries = Object.entries(specFilters).filter(([, v]) => v.size > 0);
    return products.filter((p) => {
      if (q) {
        const specHay = (p.specifications ?? [])
          .map((s) => `${s.label}:${s.value}`)
          .join(" ")
          .toLowerCase();
        const prosCons = [...(p.pros ?? []), ...(p.cons ?? [])].join(" ").toLowerCase();
        const hit =
          p.name.toLowerCase().includes(q) ||
          (p.category ?? "").toLowerCase().includes(q) ||
          (p.brand ?? "").toLowerCase().includes(q) ||
          (p.sku ?? "").toLowerCase().includes(q) ||
          (p.slug ?? "").toLowerCase().includes(q) ||
          specHay.includes(q) ||
          prosCons.includes(q);
        if (!hit) return false;
      }
      for (const [label, values] of specEntries) {
        const match = (p.specifications ?? []).find((s) => s.label === label);
        if (!match || !values.has(match.value)) return false;
      }
      return true;
    });
  }, [products, search, specFilters]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    switch (sortBy) {
      case "name_asc": arr.sort((a, b) => a.name.localeCompare(b.name, "nl")); break;
      case "name_desc": arr.sort((a, b) => b.name.localeCompare(a.name, "nl")); break;
      case "price_asc": arr.sort((a, b) => a.price_cents - b.price_cents); break;
      case "price_desc": arr.sort((a, b) => b.price_cents - a.price_cents); break;
      case "stock_asc": arr.sort((a, b) => (a.stock ?? -1) - (b.stock ?? -1)); break;
      case "stock_desc": arr.sort((a, b) => (b.stock ?? -1) - (a.stock ?? -1)); break;
      case "updated_asc": arr.sort((a, b) => a.updated_at.localeCompare(b.updated_at)); break;
      case "updated_desc":
      default: arr.sort((a, b) => b.updated_at.localeCompare(a.updated_at)); break;
    }
    return arr;
  }, [filtered, sortBy]);

  const toggleSpecFilter = (label: string, value: string) => {
    setPage(0);
    setSpecFilters((prev) => {
      const next = { ...prev };
      const cur = new Set(next[label] ?? []);
      if (cur.has(value)) cur.delete(value); else cur.add(value);
      if (cur.size === 0) delete next[label];
      else next[label] = cur;
      return next;
    });
  };
  const resetSpecFilters = () => { setSpecFilters({}); setPage(0); };

  const activeSpecCount = Object.values(specFilters).reduce((s, v) => s + v.size, 0);



  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageProducts = useMemo(() => {
    const start = page * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page]);


  const goPage = (n: number) => setPage(Math.max(0, Math.min(n, totalPages - 1)));

  if (loading) return <div className="p-8">Laden…</div>;
  if (!isAdmin) return <div className="p-8">Geen toegang.</div>;

  const openNew = () => {
    setForm(emptyForm);
    setNewImage("");
    setRecommendedIds([]);
    setAccessoryIds([]);
    setRelationSearch("");
    setOpen(true);
  };
  const openEdit = async (p: ProductRow) => {
    setForm({
      id: p.id,
      name: p.name,
      description: p.description ?? "",
      priceEur: (p.price_cents / 100).toFixed(2),
      grossPriceEur: (p.gross_price_cents / 100).toFixed(2),
      costPriceEur: (p.cost_price_cents / 100).toFixed(2),
      unit: p.unit ?? "",
      category: p.category ?? "",
      is_active: p.is_active,
      slug: p.slug ?? "",
      brand: p.brand ?? "",
      sku: p.sku ?? "",
      stock: p.stock?.toString() ?? "",
      image_url: p.image_url ?? "",
      images: Array.isArray(p.images) ? p.images : [],
      seo_title: p.seo_title ?? "",
      seo_description: p.seo_description ?? "",
      pros: Array.isArray(p.pros) ? p.pros : [],
      cons: Array.isArray(p.cons) ? p.cons : [],
      specifications: Array.isArray(p.specifications) ? p.specifications : [],
    });
    setNewImage("");
    setRecommendedIds([]);
    setAccessoryIds([]);
    setRelationSearch("");
    setOpen(true);
    try {
      const { relations } = await listRelations({ data: { product_id: p.id } });
      setRecommendedIds(
        relations.filter((r) => r.relation_type === "recommended")
          .sort((a, b) => a.position - b.position)
          .map((r) => r.related_product_id),
      );
      setAccessoryIds(
        relations.filter((r) => r.relation_type === "accessory")
          .sort((a, b) => a.position - b.position)
          .map((r) => r.related_product_id),
      );
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const addImage = () => {
    const url = newImage.trim();
    if (!url) return;
    if (form.images.includes(url)) { toast.error("Afbeelding bestaat al"); return; }
    setForm({ ...form, images: [...form.images, url], image_url: form.image_url || url });
    setNewImage("");
  };
  const removeImage = (url: string) => {
    const images = form.images.filter((i) => i !== url);
    setForm({ ...form, images, image_url: form.image_url === url ? (images[0] ?? "") : form.image_url });
  };
  const moveImage = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= form.images.length) return;
    const images = [...form.images];
    [images[i], images[j]] = [images[j], images[i]];
    setForm({ ...form, images });
  };
  const setPrimary = (url: string) => setForm({ ...form, image_url: url });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = form.images.indexOf(String(active.id));
      const newIndex = form.images.indexOf(String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        setForm({ ...form, images: arrayMove(form.images, oldIndex, newIndex) });
      }
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const added: string[] = [];
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) { toast.error(`${file.name}: geen afbeelding`); continue; }
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: groter dan 10MB`); continue; }
        const buf = await file.arrayBuffer();
        let binary = "";
        const bytes = new Uint8Array(buf);
        const chunk = 0x8000;
        for (let i = 0; i < bytes.length; i += chunk) {
          binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
        }
        const dataBase64 = btoa(binary);
        try {
          const res = await upload({ data: { filename: file.name, contentType: file.type, dataBase64 } });
          added.push(res.url);
        } catch (e) {
          toast.error(`${file.name}: ${(e as Error).message}`);
        }
      }
      if (added.length > 0) {
        setForm((f) => ({
          ...f,
          images: [...f.images, ...added.filter((u) => !f.images.includes(u))],
          image_url: f.image_url || added[0],
        }));
        toast.success(`${added.length} afbeelding(en) geüpload`);
      }
    } finally {
      setUploading(false);
    }
  };


  return (
    <div className="h-full flex flex-col gap-4">
      <div className="flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-3xl font-bold">Producten</h1>
          <p className="text-muted-foreground">{products.length} totaal · {products.filter(p => p.is_active).length} actief</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await importCatalog({});
                toast.success(`Catalogus geïmporteerd: ${r.inserted} nieuw, ${r.updated} bijgewerkt (${r.total} totaal)`);
                qc.invalidateQueries({ queryKey: ["admin-products"] });
              } catch (e) {
                toast.error((e as Error).message);
              }
            }}
          >
            Importeer winkel-catalogus
          </Button>
          <label className="inline-flex">
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              disabled={gtinBusy}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                setGtinBusy(true);
                try {
                  const text = await file.text();
                  const rows = parseGtinCsv(text);
                  if (rows.length === 0) {
                    toast.error("Geen geldige rijen in CSV (verwacht kolommen: sku, gtin, [lens_mm], [color])");
                    return;
                  }
                  const r = await importGtin({ data: { rows } });
                  setGtinResult(r);
                  toast.success(`GTIN-import: ${r.updated} bijgewerkt van ${r.total}`);
                  qc.invalidateQueries({ queryKey: ["admin-products"] });
                } catch (err) {
                  toast.error((err as Error).message);
                } finally {
                  setGtinBusy(false);
                }
              }}
            />
            <Button asChild variant="outline" disabled={gtinBusy}>
              <span className="cursor-pointer">{gtinBusy ? "Bezig…" : "GTIN CSV importeren"}</span>
            </Button>
          </label>
          <Button variant="outline" onClick={() => setSugOpen(true)}>
            <Sparkles className="size-4 mr-1" /> EAN suggesties
          </Button>
          <Button onClick={openNew}><Plus className="size-4 mr-1" /> Nieuw product</Button>
        </div>

      </div>

      <Dialog open={!!gtinResult} onOpenChange={(o) => !o && setGtinResult(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>GTIN import resultaat</DialogTitle>
          </DialogHeader>
          {gtinResult && (
            <div className="space-y-3 text-sm">
              <div>
                <strong>{gtinResult.updated}</strong> bijgewerkt · <strong>{gtinResult.duplicates.length}</strong> duplicaten · <strong>{gtinResult.notFound.length}</strong> SKU niet gevonden · <strong>{gtinResult.errors.length}</strong> fouten · <strong>{gtinResult.skipped.length}</strong> met opmerking · {gtinResult.total} rijen totaal
              </div>
              {gtinResult.notFound.length > 0 && (
                <div>
                  <div className="font-medium mb-1">Niet gevonden SKU's</div>
                  <div className="max-h-40 overflow-auto rounded border p-2 font-mono text-xs">
                    {gtinResult.notFound.join(", ")}
                  </div>
                </div>
              )}
              {gtinResult.duplicates.length > 0 && (
                <div>
                  <div className="font-medium mb-1">Duplicate EAN's geblokkeerd</div>
                  <ul className="max-h-40 overflow-auto rounded border p-2 text-xs space-y-1">
                    {gtinResult.duplicates.map((d, i) => (
                      <li key={i}><span className="font-mono">{d.sku}</span> — EAN <span className="font-mono">{d.gtin}</span> botst met <span className="font-mono">{d.conflictsWith}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {gtinResult.errors.length > 0 && (
                <div>
                  <div className="font-medium mb-1">Fouten (ongeldig formaat / checksum / DB)</div>
                  <ul className="max-h-40 overflow-auto rounded border p-2 text-xs space-y-1">
                    {gtinResult.errors.map((er, i) => (
                      <li key={i}>
                        <span className="font-mono">{er.sku}</span> — {er.message}
                        {er.missing?.length ? <span className="text-muted-foreground"> · ontbrekend: {er.missing.join(", ")}</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {gtinResult.skipped.length > 0 && (
                <div>
                  <div className="font-medium mb-1">Rijen met ontbrekende velden</div>
                  <ul className="max-h-40 overflow-auto rounded border p-2 text-xs space-y-1">
                    {gtinResult.skipped.map((s, i) => (
                      <li key={i}><span className="font-mono">{s.sku}</span> — {s.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                Verwachte CSV-kolommen (header verplicht): <code>sku,gtin,lens_mm,color</code>. GTIN wordt gevalideerd op lengte (8/12/13/14) én mod-10 controlecijfer; dubbele EAN's binnen de CSV of tegen bestaande producten worden geblokkeerd.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setGtinResult(null)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sugOpen} onOpenChange={setSugOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="size-5" /> EAN-suggesties reviewen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                disabled={sugBusy}
                onClick={async () => {
                  setSugBusy(true);
                  try {
                    const r = await genGtinSug({ data: { onlyMissing: true, limit: 15 } });
                    toast.success(`Gescand: ${r.scanned} producten, ${r.inserted} nieuwe suggesties`);
                    qc.invalidateQueries({ queryKey: ["admin-gtin-suggestions"] });
                  } catch (e) {
                    toast.error((e as Error).message);
                  } finally {
                    setSugBusy(false);
                  }
                }}
              >
                {sugBusy ? "Bezig met zoeken…" : "Scan producten zonder EAN"}
              </Button>
              <div className="flex gap-1">
                {(["pending", "approved", "rejected", "all"] as const).map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={sugStatus === s ? "default" : "outline"}
                    onClick={() => setSugStatus(s)}
                  >
                    {s === "pending" ? "Openstaand" : s === "approved" ? "Goedgekeurd" : s === "rejected" ? "Afgewezen" : "Alle"}
                  </Button>
                ))}
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              Suggesties worden gegenereerd via externe zoekresultaten (DuckDuckGo) op basis van merk + SKU.
              Alleen geldige GTIN-8/12/13/14 codes met correct controlecijfer worden voorgesteld.
              Bij goedkeuren wordt de EAN direct op het product gezet en overige openstaande suggesties voor dat product afgewezen.
            </div>

            {sugQ.isLoading && <div className="text-sm text-muted-foreground">Laden…</div>}
            {sugQ.data && sugQ.data.rows.length === 0 && (
              <div className="text-sm text-muted-foreground py-6 text-center">
                Geen suggesties in deze weergave.
              </div>
            )}

            <div className="space-y-2">
              {(sugQ.data?.rows ?? []).map((row: any) => (
                <div key={row.id} className="rounded border p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {row.products?.brand ? <span className="text-muted-foreground">{row.products.brand} · </span> : null}
                        {row.products?.name ?? "—"}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">
                        SKU {row.sku}
                        {row.products?.gtin && (
                          <span className="ml-2 text-emerald-600">· al EAN: {row.products.gtin}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="font-mono text-sm">
                        {row.suggested_gtin}
                      </Badge>
                      <Badge
                        variant={row.confidence >= 0.7 ? "default" : row.confidence >= 0.5 ? "secondary" : "outline"}
                      >
                        {Math.round(Number(row.confidence) * 100)}%
                      </Badge>
                      <Badge variant="outline">{row.source}</Badge>
                    </div>
                  </div>
                  {row.snippet && (
                    <div className="text-xs text-muted-foreground line-clamp-2">{row.snippet}</div>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs">
                      {row.source_url && (
                        <a
                          href={row.source_url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          Bron <ExternalLink className="size-3" />
                        </a>
                      )}
                      {row.status !== "pending" && (
                        <span className="ml-2 text-muted-foreground">
                          {row.status === "approved" ? "Goedgekeurd" : "Afgewezen"}
                          {row.reviewed_at ? ` · ${new Date(row.reviewed_at).toLocaleString("nl-NL")}` : ""}
                          {row.review_note ? ` · ${row.review_note}` : ""}
                        </span>
                      )}
                    </div>
                    {row.status === "pending" && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              await rejectSug({ data: { id: row.id } });
                              toast.success("Suggestie afgewezen");
                              qc.invalidateQueries({ queryKey: ["admin-gtin-suggestions"] });
                            } catch (e) {
                              toast.error((e as Error).message);
                            }
                          }}
                        >
                          <X className="size-4 mr-1" /> Afwijzen
                        </Button>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              const r = await approveSug({ data: { id: row.id } });
                              toast.success(`EAN ${r.gtin} toegewezen`);
                              qc.invalidateQueries({ queryKey: ["admin-gtin-suggestions"] });
                              qc.invalidateQueries({ queryKey: ["admin-products"] });
                            } catch (e) {
                              toast.error((e as Error).message);
                            }
                          }}
                        >
                          <Check className="size-4 mr-1" /> Goedkeuren
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSugOpen(false)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>




      <div className="flex flex-wrap items-center gap-3 shrink-0">
        <Input
          placeholder="Zoek op naam, categorie, merk, SKU, slug, specs of voor-/nadelen…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="max-w-md"
        />
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Sorteer:</label>
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setPage(0); }}
            className="h-9 rounded-md border bg-background px-2 text-sm"
          >
            <option value="updated_desc">Bijgewerkt (nieuw → oud)</option>
            <option value="updated_asc">Bijgewerkt (oud → nieuw)</option>
            <option value="name_asc">Naam (A → Z)</option>
            <option value="name_desc">Naam (Z → A)</option>
            <option value="price_asc">Prijs (laag → hoog)</option>
            <option value="price_desc">Prijs (hoog → laag)</option>
            <option value="stock_desc">Voorraad (hoog → laag)</option>
            <option value="stock_asc">Voorraad (laag → hoog)</option>
          </select>
        </div>
        {activeSpecCount > 0 && (
          <Button size="sm" variant="ghost" onClick={resetSpecFilters}>
            <X className="size-4 mr-1" /> Wis spec-filters ({activeSpecCount})
          </Button>
        )}
      </div>

      {specOptions.length > 0 && (
        <details className="border rounded-lg bg-muted/20 shrink-0" open={activeSpecCount > 0}>
          <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium flex items-center justify-between">
            <span>Filter op specificaties</span>
            <span className="text-xs text-muted-foreground">
              {specOptions.length} label{specOptions.length === 1 ? "" : "s"}
              {activeSpecCount > 0 ? ` · ${activeSpecCount} actief` : ""}
            </span>
          </summary>
          <div className="p-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {specOptions.map((spec) => (
              <div key={spec.label} className="rounded border bg-background p-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                  {spec.label}
                </div>
                <div className="flex flex-wrap gap-1">
                  {spec.values.map((v) => {
                    const active = !!specFilters[spec.label]?.has(v);
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() => toggleSpecFilter(spec.label, v)}
                        className={`text-xs px-2 py-1 rounded-full border transition ${
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background hover:bg-muted"
                        }`}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}


      <div className="border rounded-lg overflow-auto bg-white flex-1 min-h-0">
        <table className="w-full text-sm">
          <thead className="bg-white sticky top-0 z-10">
            <tr>
              <th className="text-left p-2 w-16">Foto</th>
              <th className="text-left p-2">Naam</th>
              <th className="text-left p-2">SKU</th>
              <th className="text-left p-2">Categorie</th>
              <th className="text-right p-2">Prijs</th>
              <th className="text-right p-2">Bruto</th>
              <th className="text-right p-2">Inkoop</th>
              <th className="text-right p-2">Voorraad</th>
              <th className="text-center p-2">Actief</th>
              <th className="text-center p-2">SEO</th>
              <th className="text-left p-2">Channable</th>
              <th className="text-right p-2">Acties</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={12} className="p-4 text-center text-muted-foreground">Laden…</td></tr>
            ) : queryError ? (
              <tr>
                <td colSpan={12} className="p-6 text-center">
                  <p className="text-destructive font-medium">Fout bij het laden van producten</p>
                  <p className="text-sm text-muted-foreground mt-1">{(queryError as Error).message}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-3"
                    onClick={() => qc.invalidateQueries({ queryKey: ["admin-products"] })}
                  >Opnieuw proberen</Button>
                </td>
              </tr>
            ) : pageProducts.length === 0 ? (
              <tr><td colSpan={12} className="p-4 text-center text-muted-foreground">Geen producten</td></tr>
            ) : pageProducts.map((p) => {
              const primary = p.image_url || (Array.isArray(p.images) ? p.images[0] : null);
              const hasSeo = !!(p.seo_title || p.seo_description);
              return (
                <tr key={p.id} className="border-t hover:bg-muted/40">
                  <td className="p-2">
                    {primary ? (
                      <img src={primary} alt="" className="w-10 h-10 object-cover rounded border" loading="lazy" />
                    ) : (
                      <div className="w-10 h-10 rounded border flex items-center justify-center bg-muted text-muted-foreground"><ImageIcon className="size-4" /></div>
                    )}
                  </td>
                  <td className="p-2 font-medium">
                    {p.name}
                    {p.slug && <div className="text-xs text-muted-foreground">/{p.slug}</div>}
                  </td>
                  <td className="p-2 text-xs text-muted-foreground">{p.sku ?? "—"}</td>
                  <td className="p-2 text-muted-foreground">{p.category ?? "—"}</td>
                  <td className="p-2 text-right">€{(p.price_cents / 100).toFixed(2)}{p.unit ? ` / ${p.unit}` : ""}</td>
                  <td className="p-2 text-right text-muted-foreground">{p.gross_price_cents > 0 ? `€${(p.gross_price_cents / 100).toFixed(2)}` : "—"}</td>
                  <td className="p-2 text-right text-muted-foreground">{p.cost_price_cents > 0 ? `€${(p.cost_price_cents / 100).toFixed(2)}` : "—"}</td>
                  <td className="p-2 text-right">{p.stock ?? "—"}</td>
                  <td className="p-2 text-center">{p.is_active ? "✓" : "—"}</td>
                  <td className="p-2 text-center">{hasSeo ? <span className="text-emerald-600">✓</span> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-2 text-xs">
                    {p.channable_last_error ? (
                      <span className="text-destructive" title={p.channable_last_error}>Fout</span>
                    ) : p.channable_last_synced_at ? (
                      <span className="text-emerald-600">Synced</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Pencil className="size-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => {
                      if (confirm(`"${p.name}" verwijderen?`)) delMut.mutate(p.id);
                    }}><Trash2 className="size-4 text-destructive" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between shrink-0">
          <p className="text-sm text-muted-foreground">
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sorted.length)} van {sorted.length}
          </p>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => goPage(page - 1)} disabled={page === 0}>Vorige</Button>
            {Array.from({ length: totalPages }, (_, i) => (
              <Button
                key={i}
                size="sm"
                variant={i === page ? "default" : "outline"}
                onClick={() => goPage(i)}
                className="min-w-[2.25rem]"
              >
                {i + 1}
              </Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => goPage(page + 1)} disabled={page >= totalPages - 1}>Volgende</Button>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Product bewerken" : "Nieuw product"}</DialogTitle></DialogHeader>

          <Tabs defaultValue="basis" className="w-full">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="basis">Basis</TabsTrigger>
              <TabsTrigger value="media">Afbeeldingen ({form.images.length})</TabsTrigger>
              <TabsTrigger value="specs">USP's &amp; Specs ({form.pros.length + form.cons.length + form.specifications.length})</TabsTrigger>
              <TabsTrigger value="koppel">Gekoppeld ({recommendedIds.length + accessoryIds.length})</TabsTrigger>
              <TabsTrigger value="seo">SEO &amp; Meta</TabsTrigger>
            </TabsList>


            <TabsContent value="basis" className="space-y-3 mt-4">
              <div>
                <label className="text-sm font-medium">Naam *</label>
                <Input value={form.name} onChange={(e) => {
                  const name = e.target.value;
                  setForm({ ...form, name, slug: form.slug || slugify(name) });
                }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Slug (URL)</label>
                  <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="bijv. laadpaal-22kw" />
                </div>
                <div>
                  <label className="text-sm font-medium">SKU</label>
                  <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Beschrijving</label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} />
              </div>
              <div className="grid grid-cols-6 gap-3">
                <div>
                  <label className="text-sm font-medium">Prijs (EUR) *</label>
                  <Input value={form.priceEur} onChange={(e) => setForm({ ...form, priceEur: e.target.value })} placeholder="99.00" />
                </div>
                <div>
                  <label className="text-sm font-medium">Bruto (EUR)</label>
                  <Input value={form.grossPriceEur} onChange={(e) => setForm({ ...form, grossPriceEur: e.target.value })} placeholder="0.00" />
                </div>
                <div>
                  <label className="text-sm font-medium">Inkoop (EUR)</label>
                  <Input value={form.costPriceEur} onChange={(e) => setForm({ ...form, costPriceEur: e.target.value })} placeholder="0.00" />
                </div>
                <div>
                  <label className="text-sm font-medium">Eenheid</label>
                  <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="stuk" />
                </div>
                <div>
                  <label className="text-sm font-medium">Voorraad</label>
                  <Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="—" />
                </div>
                <div>
                  <label className="text-sm font-medium">Merk</label>
                  <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Categorie</label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <label className="text-sm">Actief (zichtbaar in shop)</label>
              </div>
            </TabsContent>

            <TabsContent value="media" className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Upload lokale afbeeldingen of plak een URL. Klik op een galerij-afbeelding om die als hoofdafbeelding te kiezen.</p>
                <span className="text-xs text-muted-foreground">{form.images.length} foto{form.images.length !== 1 ? "'s" : ""}</span>
              </div>

              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer hover:bg-muted/40 transition">
                <Upload className="size-6 text-muted-foreground" />
                <span className="text-sm font-medium">{uploading ? "Bezig met uploaden…" : "Klik om afbeeldingen te kiezen"}</span>
                <span className="text-xs text-muted-foreground">PNG, JPG, WEBP — max 10MB per bestand</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => { handleFiles(e.target.files); e.currentTarget.value = ""; }}
                />
              </label>

              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">of via URL</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              <div className="flex gap-2">
                <Input
                  value={newImage}
                  onChange={(e) => setNewImage(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImage(); } }}
                  placeholder="https://…/afbeelding.jpg"
                />
                <Button type="button" onClick={addImage}><Plus className="size-4 mr-1" /> Toevoegen</Button>
              </div>

              {form.images.length === 0 ? (
                <div className="border rounded p-8 text-center text-muted-foreground">Nog geen afbeeldingen</div>
              ) : (
                <div className="space-y-4">
                  {/* Hoofdafbeelding */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Hoofdafbeelding</label>
                    <div className="relative rounded-lg border overflow-hidden bg-muted aspect-video max-h-64 flex items-center justify-center">
                      <img
                        src={form.image_url || form.images[0]}
                        alt="Hoofdafbeelding"
                        className="w-full h-full object-contain"
                      />
                      <div className="absolute top-2 left-2 bg-amber-500 text-white text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Star className="size-3 fill-white" /> Hoofdafbeelding
                      </div>
                    </div>
                  </div>

                  {/* Galerij */}
                  {form.images.length > 1 && (
                    <div>
                      <label className="text-sm font-medium mb-2 block">Galerij — klik om als hoofdafbeelding te kiezen, sleep om te herschikken</label>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext items={form.images} strategy={rectSortingStrategy}>
                          <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                            {form.images.map((url) => (
                              <SortableGalleryImage
                                key={url}
                                url={url}
                                isPrimary={(form.image_url || form.images[0]) === url}
                                onSetPrimary={setPrimary}
                                onRemove={removeImage}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="specs" className="space-y-6 mt-4">
              <BulletEditor
                title="Pluspunten (USP's)"
                icon={<ThumbsUp className="size-4 text-emerald-600" />}
                items={form.pros}
                onChange={(pros) => setForm({ ...form, pros })}
                placeholder="bijv. WizSense AI"
                accent="emerald"
              />
              <BulletEditor
                title="Minpunten"
                icon={<ThumbsDown className="size-4 text-rose-600" />}
                items={form.cons}
                onChange={(cons) => setForm({ ...form, cons })}
                placeholder="bijv. Geen optische zoom"
                accent="rose"
              />
              <SpecEditor
                items={form.specifications}
                onChange={(specifications) => setForm({ ...form, specifications })}
              />
              <p className="text-xs text-muted-foreground">
                Specificaties worden gebruikt in de zoek- en filterfunctie. Gebruik consistente labels (bijv. "Aantal MP", "Kleur", "Lens") zodat klanten op die waardes kunnen filteren.
              </p>
            </TabsContent>

            <TabsContent value="koppel" className="space-y-6 mt-4">
              {!form.id ? (
                <p className="text-sm text-muted-foreground">Sla het product eerst op om gekoppelde producten toe te voegen.</p>
              ) : (
                <>
                  <RelationPicker
                    title="Aanbevolen producten"
                    description="Toont andere producten die klanten ook leuk vinden."
                    allProducts={products}
                    excludeId={form.id}
                    selectedIds={recommendedIds}
                    onChange={setRecommendedIds}
                    search={relationSearch}
                    onSearch={setRelationSearch}
                  />
                  <RelationPicker
                    title="Accessoires"
                    description="Producten die bij dit product passen (bijv. kabels, montagesets)."
                    allProducts={products}
                    excludeId={form.id}
                    selectedIds={accessoryIds}
                    onChange={setAccessoryIds}
                    search={relationSearch}
                    onSearch={setRelationSearch}
                  />
                </>
              )}
            </TabsContent>


            <TabsContent value="seo" className="space-y-3 mt-4">

              <div>
                <label className="text-sm font-medium">SEO titel</label>
                <Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} maxLength={60} placeholder={form.name || "Paginatitel voor zoekmachines"} />
                <p className="text-xs text-muted-foreground mt-1">{form.seo_title.length}/60 tekens (advies: max 60)</p>
              </div>
              <div>
                <label className="text-sm font-medium">Meta beschrijving</label>
                <Textarea value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} rows={3} maxLength={160} placeholder="Korte beschrijving die in Google-resultaten verschijnt" />
                <p className="text-xs text-muted-foreground mt-1">{form.seo_description.length}/160 tekens (advies: max 160)</p>
              </div>

              <div className="border rounded p-3 bg-muted/30 space-y-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Google voorbeeld</p>
                {(form.image_url || form.images[0]) && (
                  <div className="rounded overflow-hidden border bg-white max-w-xs">
                    <div className="w-full h-40 bg-white flex items-center justify-center">
                      <img
                        src={form.image_url || form.images[0]}
                        alt=""
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="p-2">
                      <div className="text-xs text-emerald-700">elvion-group.nl{form.slug ? `/product/${form.slug}` : ""}</div>
                      <div className="text-blue-700 text-base font-medium leading-tight">{form.seo_title || form.name || "Producttitel"}</div>
                      <div className="text-sm text-muted-foreground line-clamp-2">{form.seo_description || form.description || "Geen beschrijving"}</div>
                    </div>
                  </div>
                )}
                {!(form.image_url || form.images[0]) && (
                  <>
                    <div className="text-xs text-emerald-700">elvion-group.nl{form.slug ? `/product/${form.slug}` : ""}</div>
                    <div className="text-blue-700 text-base font-medium leading-tight">{form.seo_title || form.name || "Producttitel"}</div>
                    <div className="text-sm text-muted-foreground line-clamp-2">{form.seo_description || form.description || "Geen beschrijving"}</div>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !form.name || !form.priceEur}>
              {saveMut.isPending ? "Opslaan…" : "Opslaan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BulletEditor({
  title, icon, items, onChange, placeholder, accent,
}: {
  title: string;
  icon: React.ReactNode;
  items: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  accent: "emerald" | "rose";
}) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) { toast.error("Bestaat al"); return; }
    onChange([...items, v]);
    setDraft("");
  };
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const ring = accent === "emerald" ? "border-emerald-200 bg-emerald-50/40" : "border-rose-200 bg-rose-50/40";
  return (
    <div className={`rounded-lg border p-3 ${ring}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="text-xs text-muted-foreground">({items.length})</span>
      </div>
      <div className="flex gap-2 mb-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
        />
        <Button type="button" onClick={add}><Plus className="size-4 mr-1" /> Toevoegen</Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nog geen items.</p>
      ) : (
        <ul className="space-y-1">
          {items.map((it, i) => (
            <li key={`${it}-${i}`} className="flex items-center gap-2 bg-background rounded border px-2 py-1 text-sm">
              <span className="flex-1">{it}</span>
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => move(i, 1)} disabled={i === items.length - 1}>↓</button>
              <button type="button" className="text-destructive hover:opacity-80" onClick={() => remove(i)} title="Verwijderen">
                <X className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SpecEditor({
  items, onChange,
}: {
  items: Spec[];
  onChange: (next: Spec[]) => void;
}) {
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const add = () => {
    const l = label.trim();
    const v = value.trim();
    if (!l || !v) { toast.error("Label en waarde verplicht"); return; }
    if (items.some((s) => s.label.toLowerCase() === l.toLowerCase())) {
      toast.error("Label bestaat al"); return;
    }
    onChange([...items, { label: l, value: v }]);
    setLabel(""); setValue("");
  };
  const update = (i: number, patch: Partial<Spec>) => {
    onChange(items.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  };
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="rounded-lg border p-3 bg-muted/20">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-semibold">Specificaties</h3>
        <span className="text-xs text-muted-foreground">({items.length}) — gebruikt door filter &amp; zoekfunctie</span>
      </div>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-3">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (bijv. Aantal MP)"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Waarde (bijv. 8 MP)"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <Button type="button" onClick={add}><Plus className="size-4 mr-1" /> Toevoegen</Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nog geen specificaties.</p>
      ) : (
        <div className="space-y-1">
          {items.map((s, i) => (
            <div key={`${s.label}-${i}`} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
              <Input value={s.label} onChange={(e) => update(i, { label: e.target.value })} />
              <Input value={s.value} onChange={(e) => update(i, { value: e.target.value })} />
              <div className="flex items-center gap-1">
                <button type="button" className="text-xs text-muted-foreground hover:text-foreground px-1" onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
                <button type="button" className="text-xs text-muted-foreground hover:text-foreground px-1" onClick={() => move(i, 1)} disabled={i === items.length - 1}>↓</button>
                <button type="button" className="text-destructive hover:opacity-80 p-1" onClick={() => remove(i)} title="Verwijderen">
                  <X className="size-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
